import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PlusIcon, ScaleIcon, UploadIcon } from '@repo/ui/icons'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import { QualificationTags } from '../../components/elements/qualification-badge'
import { StaffPayScalesFilter } from '../../components/pages/staff-pay-scales/staff-pay-scales-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { getQualificationsCurrencySymbol } from '../../lib/types/enums/staff/staff-qualifications.enum'
import {
  PayScaleTypeEnum,
  payScaleTypeEnumMapping
} from '../../lib/types/enums/staff-pay-scale/pay-scale-type.enum'
import {
  StaffPayScale,
  StaffPayScaleFilters
} from '../../lib/types/models/staff-pay-scale/staff-pay-cale'
import {
  transformStaffPayScaleFiltersToUrl,
  transformUrlToStaffPayScaleFilters
} from '../../lib/utils/filters-mapping/staff-pay-scale'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

export const staffInitialFilter: StaffPayScaleFilters = {
  type: undefined,
  position: undefined,
  countries: [],
  formulas: [],
  periods: [],
  qualifications: []
}

export function StaffPayScalesListPage() {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = React.useRef<DynamicTableRef<StaffPayScale>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { t } = useTranslate()

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<StaffPayScaleFilters>({
      initialFilters: staffInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformStaffPayScaleFiltersToUrl,
      transformUrlToFilter: transformUrlToStaffPayScaleFilters
    })

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const columns: Column<StaffPayScale>[] = [
    {
      enableSorting: true,
      accessorKey: 'name',
      header: 'NOM'
    },
    {
      enableSorting: true,
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge
          text={payScaleTypeEnumMapping[row.original.type].label}
          variant={payScaleTypeEnumMapping[row.original.type].variant}
        />
      )
    },
    {
      enableSorting: true,
      accessorKey: 'position',
      header: 'Poste',
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.position])
    },
    {
      accessorKey: 'diplomas',
      header: 'Diplômes',
      cell: ({ row }) =>
        row.original.diplomas.length === 0 ? (
          <span>-</span>
        ) : (
          <div className={'flex flex-wrap w-fit  gap-1'}>
            {row.original.diplomas.map((diploma, index) => (
              <Badge key={index} text={diploma} variant={'white'} />
            ))}
          </div>
        )
    },
    {
      accessorKey: 'qualifications',
      header: 'Qualifications',
      cell: ({ row }) =>
        row.original.qualifications?.length === 0 ? (
          <span>-</span>
        ) : (
          <QualificationTags qualifications={row.original.qualifications ?? []} />
        )
    },
    {
      accessorKey: 'id',
      header: 'Salaire',
      cell: ({ row }) => {
        const data = row.original
        const currencySymbol = getQualificationsCurrencySymbol(data)
        if (data.type === PayScaleTypeEnum.DAILY_SALARY)
          return (
            <div className={'flex  gap-1'}>
              <Badge
                variant={'blue'}
                textColor={tailwindColors.grey.DEFAULT}
                text={
                  data.dailySalaryFromFirstSlice
                    ? parseFloat(data.dailySalaryFromFirstSlice.toFixed(2)) + currencySymbol
                    : '-'
                }
              />
              <Badge
                variant={'blue'}
                textColor={tailwindColors.grey.DEFAULT}
                text={
                  data.dailySalaryFromSecondSlice
                    ? parseFloat(data.dailySalaryFromSecondSlice.toFixed(2)) + currencySymbol
                    : '-'
                }
              />
            </div>
          )

        return (
          <div className={'flex gap-1'}>
            <Badge
              variant={'blue'}
              textColor={tailwindColors.grey.DEFAULT}
              text={
                data.bonusFromFirstSlice != null
                  ? parseFloat(data.bonusFromFirstSlice.toFixed(2)) + currencySymbol
                  : '-'
              }
            />
            <Badge
              variant={'blue'}
              textColor={tailwindColors.grey.DEFAULT}
              text={
                data.bonusFromSecondSlice != null
                  ? parseFloat(data.bonusFromSecondSlice.toFixed(2)) + currencySymbol
                  : '-'
              }
            />
          </div>
        )
      }
    },
    {
      accessorKey: 'countries',
      header: 'Pays',
      cell: ({ row }) => (
        <div className="flex flex-wrap w-20 gap-1">
          {row.original.countries.length > 0
            ? row.original.countries.map((country) => (
                <CountryFlag possibleCountry={country.name} key={country.id} />
              ))
            : '-'}
        </div>
      )
    },
    {
      accessorKey: 'periods',
      header: 'Périodes',
      cell: ({ row }) =>
        row.original.periods.length === 0 ? (
          <span>-</span>
        ) : (
          <div className={'flex flex-wrap py-1  gap-1'}>
            {row.original.periods.map((period, index) => (
              <PeriodBadge key={index} period={period} />
            ))}
          </div>
        )
    },
    {
      accessorKey: 'formulas',
      header: 'Formules',
      cell: ({ row }) =>
        row.original.formulas.length === 0 ? (
          <span>-</span>
        ) : (
          <div className={'flex flex-wrap  gap-1'}>
            {row.original.formulas.map((formula, index) => (
              <Badge key={index} text={getTranslatedName(formula)} variant={'white'} />
            ))}
          </div>
        )
    }
  ]

  return (
    <div>
      <UserNavBar title={'Barèmes de rémunération'} icon={ScaleIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<StaffPayScale, StaffPayScaleFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Liste des barèmes'}
        deleteMessage={{
          name: 'barèmes',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce barème ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces barèmes ?'
        }}
        columns={columns}
        selectable
        allowSearchParam
        version={'FULL'}
        endpoint={API.staffPayScales.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        onRowClick={(staffPayScale, event) =>
          navigateWithEvent(
            Paths.STAFF_PAY_SCALE_UPDATE_PAGE.replace(':id', staffPayScale.id.toString()),
            event
          )
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter un barème',
            fn: () => navigate(Paths.STAFF_PAY_SCALE_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter vers Excel',
            fn: (staffs) => {
              const ids = staffs.map((staff) => staff.id)
              downloadDocuments({
                endpoint: API.staffPayScales.exportExcel.url,
                ids
              })
            }
          }
        ]}
      />

      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <StaffPayScalesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
